import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

/**
 * A surface that stepped out of the page but can still come back.
 *
 * Every window keeps its own lifecycle, so the dock only stores the label
 * it shows and the callbacks that drive that window.
 */
export type SurfaceDockEntry = {
	/** Stable id of the surface, used to update or remove the entry. */
	id: string;
	/** Window title, usually a filename or a document name. */
	title: string;
	/** Provenance line under the title, e.g. `~/ inspector`. */
	caption?: string;
	/** Iconify name rendered in the entry tile. */
	icon?: string;
	/** Brings the surface back. */
	restore: () => void;
	/** Drops the surface for good. */
	close: () => void;
};

type SurfaceDockContextValue = {
	entries: SurfaceDockEntry[];
	register: (entry: SurfaceDockEntry) => void;
	unregister: (id: string) => void;
};

const SurfaceDockContext = createContext<SurfaceDockContextValue | null>(null);

export function SurfaceDockProvider({ children }: PropsWithChildren) {
	const [entries, setEntries] = useState<SurfaceDockEntry[]>([]);

	const register = useCallback((entry: SurfaceDockEntry) => {
		setEntries((current) => {
			const index = current.findIndex((item) => item.id === entry.id);
			if (index === -1) return [...current, entry];
			// NOTE: re-registering refreshes the callbacks but keeps the
			// position, so a retitled window does not jump the stack.
			if (current[index] === entry) return current;
			const next = [...current];
			next[index] = entry;
			return next;
		});
	}, []);

	const unregister = useCallback((id: string) => {
		setEntries((current) => current.filter((entry) => entry.id !== id));
	}, []);

	const value = useMemo(
		() => ({ entries, register, unregister }),
		[entries, register, unregister],
	);

	return (
		<SurfaceDockContext.Provider value={value}>
			{children}
		</SurfaceDockContext.Provider>
	);
}

export function useSurfaceDock() {
	const context = useContext(SurfaceDockContext);

	if (!context) {
		throw new Error('useSurfaceDock must be used inside a SurfaceDockProvider');
	}

	return context;
}
